/**
 * Clinic Document Chat Slice
 *
 * Redux slice for managing clinic document Q&A chat state.
 * Follows the admin agent chat pattern adapted for document retrieval.
 */

import {
  type ActivityType,
  type AssistantStatus,
  type ChatMessage,
  type SSEPerformanceMetrics,
} from '@features/ai-chat-core';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types
// ============================================================================

export interface DocToolCall {
  toolCallId: string;
  toolCallName: string;
  args: string;
}

export interface ClinicDocChatState {
  // Session
  threadId: string | null;

  // Messages
  messages: ChatMessage[];

  // Activity status messages
  activities: Record<
    string,
    {
      messageId: string;
      activityType: ActivityType;
      message: string;
      timestamp: number;
    }
  >;

  // Streaming state
  assistant: {
    status: AssistantStatus;
    currentStep: string | null;
    streamingContent: string;
    currentMessageId: string | null;
    firstTokenReceived: boolean;
  };

  // Active tool calls (for "Searching Documents" progress)
  activeToolCalls: DocToolCall[];

  // Error handling
  error: string | null;

  // SSE performance metrics
  ssePerformanceMetrics: SSEPerformanceMetrics | null;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: ClinicDocChatState = {
  threadId: null,
  messages: [],
  activities: {},
  assistant: {
    status: 'idle',
    currentStep: null,
    streamingContent: '',
    currentMessageId: null,
    firstTokenReceived: false,
  },
  activeToolCalls: [],
  error: null,
  ssePerformanceMetrics: null,
};

// ============================================================================
// Slice
// ============================================================================

export const clinicDocChatSlice = createSlice({
  name: 'clinicDocuments/chat',
  initialState,
  reducers: {
    setThreadId: (state, action: PayloadAction<string>) => {
      state.threadId = action.payload;
    },

    addUserMessage: (state, action: PayloadAction<string>) => {
      const now = new Date();
      state.messages.push({
        id: `user-${now.getTime()}`,
        role: 'user',
        content: action.payload,
        timestamp: now.toISOString(),
      });
    },

    setAssistantStatus: (state, action: PayloadAction<AssistantStatus>) => {
      state.assistant.status = action.payload;
    },

    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.assistant.currentStep = action.payload;
    },

    startStreamingMessage: (state, action: PayloadAction<string>) => {
      state.assistant.currentMessageId = action.payload;
      state.assistant.streamingContent = '';
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'streaming';
    },

    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.assistant.streamingContent += action.payload;
      if (!state.assistant.firstTokenReceived) {
        state.assistant.firstTokenReceived = true;
      }
    },

    finalizeMessage: state => {
      if (state.assistant.streamingContent) {
        const now = new Date();
        state.messages.push({
          id: state.assistant.currentMessageId || `assistant-${now.getTime()}`,
          role: 'assistant',
          content: state.assistant.streamingContent,
          timestamp: now.toISOString(),
        });
      }
      state.assistant.streamingContent = '';
      state.assistant.currentMessageId = null;
      state.assistant.firstTokenReceived = false;
      state.assistant.status = 'thinking';
      state.activities = {};
    },

    setActivitySnapshot: (
      state,
      action: PayloadAction<{
        messageId: string;
        activityType: ActivityType;
        message: string;
        timestamp: number;
        replace?: boolean;
      }>,
    ) => {
      const { messageId, activityType, message, timestamp, replace = true } = action.payload;
      if (!replace && state.activities[messageId]) return;
      state.activities[messageId] = { messageId, activityType, message, timestamp };
    },

    addToolCall: (
      state,
      action: PayloadAction<{ toolCallId: string; toolCallName: string }>,
    ) => {
      state.activeToolCalls.push({
        toolCallId: action.payload.toolCallId,
        toolCallName: action.payload.toolCallName,
        args: '',
      });
    },

    appendToolCallArgs: (
      state,
      action: PayloadAction<{ toolCallId: string; delta: string }>,
    ) => {
      const toolCall = state.activeToolCalls.find(
        tc => tc.toolCallId === action.payload.toolCallId,
      );
      if (toolCall) {
        toolCall.args += action.payload.delta;
      }
    },

    removeToolCall: (state, action: PayloadAction<string>) => {
      state.activeToolCalls = state.activeToolCalls.filter(
        tc => tc.toolCallId !== action.payload,
      );
    },

    clearToolCalls: state => {
      state.activeToolCalls = [];
    },

    completeWorkflow: state => {
      state.assistant.status = 'idle';
      state.assistant.currentStep = null;
      state.activities = {};
      state.activeToolCalls = [];
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.assistant.status = 'idle';
      state.activeToolCalls = [];
    },

    clearError: state => {
      state.error = null;
    },

    setSSEPerformanceMetrics: (state, action: PayloadAction<SSEPerformanceMetrics>) => {
      state.ssePerformanceMetrics = action.payload;
    },

    resetChat: () => initialState,

    newConversation: state => {
      state.threadId = null;
      state.messages = [];
      state.activities = {};
      state.activeToolCalls = [];
      state.assistant = initialState.assistant;
      state.error = null;
    },
  },
});

export const {
  setThreadId,
  addUserMessage,
  setAssistantStatus,
  setCurrentStep,
  startStreamingMessage,
  appendStreamingContent,
  finalizeMessage,
  setActivitySnapshot,
  addToolCall,
  appendToolCallArgs,
  removeToolCall,
  clearToolCalls,
  completeWorkflow,
  setError,
  clearError,
  setSSEPerformanceMetrics,
  resetChat,
  newConversation,
} = clinicDocChatSlice.actions;

export default clinicDocChatSlice.reducer;
